import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Use relative URL for API calls to avoid CORS issues in production
const API = '/api';
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'eliabonacci92@gmail.com';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      
      // Ensure role and is_approved fields exist
      userData.role = userData.role || (userData.email === ADMIN_EMAIL ? 'admin' : 'user');
      userData.is_approved = userData.is_approved ?? (userData.email === ADMIN_EMAIL);
      
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Refresh user data (e.g., after profile photo upload)
  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      userData.role = userData.role || (userData.email === ADMIN_EMAIL ? 'admin' : 'user');
      userData.is_approved = userData.is_approved ?? (userData.email === ADMIN_EMAIL);
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, ...userData } = response.data;
    
    // Ensure role and is_approved fields exist
    userData.role = userData.role || (userData.email === ADMIN_EMAIL ? 'admin' : 'user');
    userData.is_approved = userData.is_approved ?? (userData.email === ADMIN_EMAIL);
    
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return response.data;
  };

  const register = async (name, email, password, ref = null) => {
    const payload = { name, email, password };
    if (ref) payload.ref = ref;
    
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken, ...userData } = response.data;
    
    // Ensure role and is_approved fields exist
    userData.role = userData.role || (userData.email === ADMIN_EMAIL ? 'admin' : 'user');
    userData.is_approved = userData.is_approved ?? (userData.email === ADMIN_EMAIL);
    
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('impersonated-user');
    localStorage.removeItem('hedging-tracker-module');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const verifyPassword = async (password) => {
    const response = await axios.post(`${API}/auth/verify-password`, { password });
    return response.data.valid;
  };

  // Cambio profilo collegato: nuovo token dal server, poi ricarico l'app da zero
  const switchProfile = async (targetUserId) => {
    const response = await axios.post(`${API}/auth/switch-profile`, { target_user_id: targetUserId });
    const newToken = response.data.token;
    localStorage.clear();
    sessionStorage.clear();
    localStorage.setItem('token', newToken);
    window.location.replace('/arbicsx/dashboard');
  };

  const isAdmin = user?.email === ADMIN_EMAIL || user?.role === 'admin' || user?.role === 'admin_primary' || user?.role === 'admin_secondary';
  const isApproved = user?.is_approved || isAdmin;

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    verifyPassword,
    switchProfile,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin,
    isApproved
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
